/**
 * ClipData — engine-neutral animation clips (ADR-003 §2c): typed-array tracks,
 * explicit timebase, and a NAMED root-motion channel so facing correction and
 * hip-delta scaling never guess.
 */
import { z } from 'zod';
import { SchemaValidationError } from './errors.js';

export const trackPathSchema = z.enum(['rotation', 'translation', 'scale', 'morph']);
export type TrackPath = z.infer<typeof trackPathSchema>;

const float32 = z.instanceof(Float32Array);

export const trackSchema = z
  .object({
    /** Rig bone name (or morph target name for morph tracks). */
    target: z.string().min(1),
    path: trackPathSchema,
    /** Keyframe times in seconds, ascending. */
    times: float32,
    /** rotation: 4/key (xyzw) · translation/scale: 3/key · morph: 1/key. */
    values: float32,
  })
  .refine((t) => t.times.length > 0, { message: 'track has no keyframes' })
  .refine(
    (t) => {
      const stride = t.path === 'rotation' ? 4 : t.path === 'morph' ? 1 : 3;
      return t.values.length === t.times.length * stride;
    },
    { message: 'values length does not match times × stride for this path' },
  );
export type ClipTrack = z.infer<typeof trackSchema>;

export const clipDataSchema = z
  .object({
    name: z.string().min(1),
    /** Frames per second the clip was authored at (timebase; times stay in seconds). */
    fps: z.number().positive(),
    durationSec: z.number().positive(),
    /** Absolute pose tracks vs additive layer. */
    additive: z.boolean(),
    /**
     * The rig bone carrying root motion (usually Hips) — REQUIRED to be
     * declared (null = explicitly none) so downstream corrections operate on
     * a named channel, never a guessed one (ADR-003 §2c).
     */
    rootMotionTarget: z.string().nullable(),
    tracks: z.array(trackSchema).min(1),
  })
  .refine(
    (c) =>
      c.rootMotionTarget === null ||
      c.tracks.some((t) => t.target === c.rootMotionTarget && t.path === 'translation'),
    {
      // Root motion is consumed from the TRANSLATION channel only — a
      // declaration satisfied by a rotation track would validate and then
      // silently produce no root motion (Codex, PR #13).
      message: 'rootMotionTarget names a bone with no translation track in this clip',
    },
  );
export type ClipData = z.infer<typeof clipDataSchema>;

/** Validate raw data into ClipData, loudly (ADR-003 §7). */
export function parseClipData(raw: unknown): ClipData {
  const parsed = clipDataSchema.safeParse(raw);
  if (!parsed.success) {
    throw new SchemaValidationError(
      'ClipData',
      parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`),
    );
  }
  const clip = parsed.data;
  const problems: string[] = [];
  for (const t of clip.tracks) {
    // Non-finite samples would poison all downstream math while passing the
    // ascending check (NaN <= prev is false) — reject at the boundary
    // (Codex review, PR #2).
    for (let i = 0; i < t.times.length; i += 1) {
      if (!Number.isFinite(t.times[i])) {
        problems.push(`track "${t.target}/${t.path}": non-finite time at index ${i}`);
        break;
      }
    }
    for (let i = 0; i < t.values.length; i += 1) {
      if (!Number.isFinite(t.values[i])) {
        problems.push(`track "${t.target}/${t.path}": non-finite value at index ${i}`);
        break;
      }
    }
    for (let i = 1; i < t.times.length; i += 1) {
      const prev = t.times[i - 1] as number;
      const cur = t.times[i] as number;
      if (cur <= prev) {
        problems.push(`track "${t.target}/${t.path}": times not strictly ascending at index ${i}`);
        break;
      }
    }
  }
  if (problems.length > 0) throw new SchemaValidationError('ClipData', problems);
  return clip;
}
